import React, { useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import head from 'lodash/head';

import {
  ChangeLifestylesFiltersFunc,
  FetchLifestylesCacheKey,
  FetchLifestylesFilterFields,
  FetchLifestylesFilterLifestyles,
  FetchLifestylesFilters,
  FetchLifestylesLimit,
  FetchLifestylesPage,
  FetchLifestylesSort,
  FetchLifestylesSortTypes,
  LifestyleCategoryID,
  LifestyleNanoID,
  OnLifestyleSimilarSearchAction,
  UpdateLifestyleCacheAction
} from '../../../../lifestyles/lifestylesTypes';
import { TeamNanoID, TeamUUID } from '../../../../teams/teamsTypes';
import {
  ErrorMessage,
  FetchItemsLoadMoreItems,
  FetchItemsSort,
  IdFilterType,
  IsFetched
} from '../../../../../types';
import {
  FetchLifestylesSetsCacheKeys,
  UpdateLifestylesSetCacheAction
} from '../../../../lifestylesSets/lifestylesSetsTypes';

import { useThreeDStockContentView } from '../../../hooks/useThreeDStockContentView';
import { useThreeDStockContentScale } from '../../../hooks/useThreeDStockContentScale';
import { useLifestylesSortDropdown } from '../../../../lifestyles/hooks/useLifestylesSortDropdown';
import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useThreeDStockLifestylesContentCheckedCategories } from './hooks/useThreeDStockLifestylesContentCheckedCategories';

import { ThreeDStockContentHeader } from '../../headers/ThreeDStockContentHeader';
import { ThreeDStockContentSortAndView } from '../../headers/ThreeDStockContentSortAndView';
import { ThreeDStockContentSortDropdownButtonButtonsType } from '../../headers/ThreeDStockContentSortAndView/components/ThreeDStockContentSortDropdownButton';
import {
  ThreeDStockLifestylesList,
  ThreeDStockLifestylesListLifestyle,
  ThreeDStockLifestylesListLifestyles,
  ThreeDStockLifestylesListLifestylesSet
} from '../../lists/ThreeDStockLifestylesList';
import { ThreeDStockLifestylesCompactList } from '../../lists/ThreeDStockLifestylesCompactList';
import { ThreeDStockLifestylesFilters } from '../../filters/ThreeDStockLifestylesFilters';

import { isEveryPropEmpty } from '../../../../../utils/isEveryPropEmpty';

import { StockTabs, StockPermissions } from '../../../stockConstants';
import { LifestylesPermissions } from '../../../../lifestyles/lifestylesConstants';

import { words } from '../../../../../locales/keys';

const lifestylesSortI18nTextTypes = {
  [FetchLifestylesSortTypes.CREATED_AT_DESC]: words.newest,
  [FetchLifestylesSortTypes.SELECTED_LIFESTYLES_COUNT_DESC]: words.mostUsed,
  [FetchLifestylesSortTypes.USERS_CHOSEN_LIFESTYLES_COUNT_DESC]: words.mostLiked
};

const allFilterFields = [
  FetchLifestylesFilterFields.SIMILAR_LIFESTYLE_IMAGE_ID,
  FetchLifestylesFilterFields.MATERIAL_IDS,
  FetchLifestylesFilterFields.PREMIUM_CONTENT_AT,
  FetchLifestylesFilterFields.STYLE_IDS,
  FetchLifestylesFilterFields.GAMMA_ID,
  FetchLifestylesFilterFields.TONE_ID,
  FetchLifestylesFilterFields.MAIN_COLOR_IDS,
  FetchLifestylesFilterFields.BLOCKED,
  FetchLifestylesFilterFields.LIFESTYLE_TYPE_ID,
  FetchLifestylesFilterFields.FAVORITE,
  FetchLifestylesFilterFields.CURRENT_TEAM_FAVORITE,
  FetchLifestylesFilterFields.TEAM_ID_FAVORITE
];

const tab = StockTabs.LIFESTYLES;

interface ThreeDStockLifestylesContentProps {
  lifestylesCacheKey: FetchLifestylesCacheKey;
  lifestylesFilters: FetchLifestylesFilters;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  filterLifestyles: FetchLifestylesFilterLifestyles;
  lifestylesSort: FetchItemsSort;
  sortLifestyles: (nextSort: FetchLifestylesSort) => void;
  lifestyles: ThreeDStockLifestylesListLifestyles;
  updateLifestyleCache?: UpdateLifestyleCacheAction<ThreeDStockLifestylesListLifestyle>;
  onLifestyleEditButtonMouseEnter?: (lifestyleNanoId: LifestyleNanoID) => void;
  lifestylesError: ErrorMessage;
  lifestylesFetched: IsFetched;
  lifestylesIsPlaceholderData: boolean;
  lifestylesTotalCount: number;
  hasNextLifestylesPage: boolean;
  lifestylesPage: FetchLifestylesPage;
  lifestylesLimit: FetchLifestylesLimit;
  loadMoreLifestyles: FetchItemsLoadMoreItems;
  lifestylesFetchingNextPage: boolean;
  lifestylesSet: ThreeDStockLifestylesListLifestylesSet | null;
  lifestylesSetCacheKeys?: FetchLifestylesSetsCacheKeys;
  updateLifestylesSetCache: UpdateLifestylesSetCacheAction<ThreeDStockLifestylesListLifestylesSet>;
  changeLifestylesCategoryFilters: (
    tab?: StockTabs,
    changeFilters?: FetchLifestylesFilters,
    removeFilters?: string[]
  ) => void;
  defaultLifestyleCategoryId?: IdFilterType;
  defaultLifestyleParentCategoryIds?: LifestyleCategoryID[];
  clearLifestylesFilters?: (
    persistFiltersFields?: FetchLifestylesFilterFields[]
  ) => void;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  teamUuid?: TeamUUID;
  withPremiumContentAtFilter?: boolean;
  withRealAspectRatio?: boolean;
  isModal?: boolean;
  onLifestyleSimilarSearchFilter?: OnLifestyleSimilarSearchAction;
}

function ThreeDStockLifestylesContent({
  lifestylesCacheKey,
  lifestylesFilters,
  defaultLifestyleCategoryId,
  defaultLifestyleParentCategoryIds,
  clearLifestylesFilters,
  changeLifestylesFilters,
  filterLifestyles,
  lifestylesSort,
  sortLifestyles,
  lifestyles,
  updateLifestyleCache,
  onLifestyleEditButtonMouseEnter,
  lifestylesError,
  lifestylesFetched,
  lifestylesIsPlaceholderData,
  lifestylesTotalCount,
  hasNextLifestylesPage,
  lifestylesPage,
  lifestylesLimit,
  loadMoreLifestyles,
  lifestylesFetchingNextPage,
  lifestylesSet,
  lifestylesSetCacheKeys,
  updateLifestylesSetCache,
  changeLifestylesCategoryFilters,
  companyNanoId,
  teamUuid,
  isMyLibrary,
  withPremiumContentAtFilter,
  withRealAspectRatio,
  isModal,
  onLifestyleSimilarSearchFilter
}: ThreeDStockLifestylesContentProps) {
  const currentUser = useCurrentUser();

  const [showFilters, setShowFilters] = useState(true);

  const { checkedCategories, setCheckedCategories } =
    useThreeDStockLifestylesContentCheckedCategories();

  useEffect(
    () => {
      if (!isEmpty(checkedCategories)) {
        setCheckedCategories([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultLifestyleCategoryId]
  );

  const closeFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const openFilters = useCallback(() => {
    setShowFilters(true);
  }, []);

  const withFilters = useMemo<boolean>(
    () =>
      !isEveryPropEmpty(pick(lifestylesFilters, allFilterFields)) ||
      !isEmpty(checkedCategories),
    [lifestylesFilters, checkedCategories]
  );

  const { isGridView } = useThreeDStockContentView({ tab });
  const { scale } = useThreeDStockContentScale({ tab });

  const { handleSortByMostLiked, handleSortByMostUsed, handleSortByNewest } =
    useLifestylesSortDropdown({
      lifestylesBaseCacheKey: lifestylesCacheKey,
      sortLifestyles
    });

  const handleChangeCategory = useCallback<
    (changeFilters?: FetchLifestylesFilters, removeFilters?: string[]) => void
  >(
    (changeFilters, removeFilters) => {
      changeLifestylesCategoryFilters(
        StockTabs.LIFESTYLES,
        changeFilters,
        removeFilters
      );
    },
    [changeLifestylesCategoryFilters]
  );

  const sortBy = useMemo(() => head(lifestylesSort), [lifestylesSort]);

  const sortButtons = useMemo<
    ThreeDStockContentSortDropdownButtonButtonsType[]
  >(
    () => [
      {
        id: 'newest',
        i18nText: words.newest,
        onSelect: handleSortByNewest,
        active: sortBy === FetchLifestylesSortTypes.CREATED_AT_DESC
      },
      {
        id: 'mostUsed',
        i18nText: words.mostUsed,
        onSelect: handleSortByMostUsed,
        active:
          sortBy === FetchLifestylesSortTypes.SELECTED_LIFESTYLES_COUNT_DESC
      },
      {
        id: 'mostLiked',
        i18nText: words.mostLiked,
        onSelect: handleSortByMostLiked,
        active:
          sortBy === FetchLifestylesSortTypes.USERS_CHOSEN_LIFESTYLES_COUNT_DESC
      }
    ],
    [handleSortByMostLiked, handleSortByMostUsed, handleSortByNewest, sortBy]
  );

  return (
    <div
      id="3d-stock-container"
      className="items-start p-4 md:p-8 flex flex-col gap-8 2xl:gap-x-11"
    >
      <ThreeDStockContentHeader
        tab={tab}
        categoryIds={defaultLifestyleCategoryId?.in}
        changeItemsCategoryFilters={changeLifestylesCategoryFilters}
        isLibrary={isMyLibrary || !!companyNanoId}
      />

      <div
        id="3d-stock-wrapper"
        className="flex w-full gap-8 2xl:gap-x-11 items-start"
      >
        {showFilters && (
          <ThreeDStockLifestylesFilters
            teamUuid={teamUuid}
            changeLifestylesFilters={changeLifestylesFilters}
            filterLifestyles={filterLifestyles}
            lifestylesFilters={lifestylesFilters}
            closeFilters={closeFilters}
            clearFilters={clearLifestylesFilters}
            defaultLifestyleCategoryId={defaultLifestyleCategoryId}
            defaultLifestyleParentCategoryIds={
              defaultLifestyleParentCategoryIds
            }
            checkedCategories={checkedCategories}
            setCheckedCategories={setCheckedCategories}
            changeCategory={handleChangeCategory}
            clientIds={lifestylesFilters?.clientIds}
            withPremiumContentAtFilter={withPremiumContentAtFilter}
            isModal={isModal}
          />
        )}

        <div className="flex flex-col w-full gap-8 2xl:gap-x-11 min-w-0">
          <ThreeDStockContentSortAndView
            tab={tab}
            currentSortI18nText={lifestylesSortI18nTextTypes[sortBy]}
            sortButtons={sortButtons}
            totalCount={lifestylesTotalCount}
            page={lifestylesPage}
            limit={lifestylesLimit}
            searchText={lifestylesFilters.nameTags}
            withOpenFiltersButton={!showFilters}
            withSelectedFilters={withFilters}
            openFilters={openFilters}
            withTotal={currentUser.hasPermissions(
              LifestylesPermissions.READ_LIFESTYLES_TOTAL_COUNT
            )}
            withImageScaleButton={currentUser.hasPermissions(
              LifestylesPermissions.READ_LIFESTYLES_IMAGE_SCALE
            )}
            withImageToggleViewButton={currentUser.hasPermissions(
              LifestylesPermissions.READ_LIFESTYLES_TOGGLE_VIEW_BUTTON
            )}
            withEditLifestylesButton={
              !isModal &&
              !companyNanoId &&
              !isMyLibrary &&
              currentUser.hasPermissions(
                StockPermissions.READ_LIFESTYLES_EDIT_BUTTON
              )
            }
            withCompanyLibraryEditLifestylesButton={
              !isModal &&
              (isMyLibrary || !!companyNanoId) &&
              currentUser.hasPermissions(
                isMyLibrary
                  ? LifestylesPermissions.READ_SELF_COMPANY_LIBRARY_EDIT_LIFESTYLES_BUTTON
                  : LifestylesPermissions.READ_OTHER_COMPANY_LIBRARY_EDIT_LIFESTYLES_BUTTON
              )
            }
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
          />

          {isGridView ? (
            <ThreeDStockLifestylesList
              cellMinSize={scale}
              onLifestyleEditButtonMouseEnter={onLifestyleEditButtonMouseEnter}
              lifestyles={lifestyles}
              lifestylesError={lifestylesError}
              lifestylesFetched={lifestylesFetched}
              lifestylesIsPlaceholderData={lifestylesIsPlaceholderData}
              lifestylesTotalCount={lifestylesTotalCount}
              updateLifestyleCache={updateLifestyleCache}
              changeLifestylesFilters={changeLifestylesFilters}
              lifestylesFilters={lifestylesFilters}
              hasNextLifestylesPage={hasNextLifestylesPage}
              lifestylesPage={lifestylesPage}
              lifestylesLimit={lifestylesLimit}
              loadMoreLifestyles={loadMoreLifestyles}
              lifestylesFetchingNextPage={lifestylesFetchingNextPage}
              lifestylesSet={lifestylesSet}
              updateLifestylesSetCache={updateLifestylesSetCache}
              lifestylesSetCacheKeys={lifestylesSetCacheKeys}
              companyNanoId={companyNanoId}
              isMyLibrary={isMyLibrary}
              withRealAspectRatio={withRealAspectRatio}
              onLifestyleSimilarSearchFilter={onLifestyleSimilarSearchFilter}
            />
          ) : (
            <ThreeDStockLifestylesCompactList
              onLifestyleEditButtonMouseEnter={onLifestyleEditButtonMouseEnter}
              lifestyles={lifestyles}
              lifestylesError={lifestylesError}
              lifestylesFetched={lifestylesFetched}
              lifestylesIsPlaceholderData={lifestylesIsPlaceholderData}
              lifestylesTotalCount={lifestylesTotalCount}
              changeLifestylesFilters={changeLifestylesFilters}
              lifestylesFilters={lifestylesFilters}
              hasNextLifestylesPage={hasNextLifestylesPage}
              lifestylesPage={lifestylesPage}
              lifestylesLimit={lifestylesLimit}
              loadMoreLifestyles={loadMoreLifestyles}
              lifestylesFetchingNextPage={lifestylesFetchingNextPage}
              updateLifestyleCache={updateLifestyleCache}
              companyNanoId={companyNanoId}
              isMyLibrary={isMyLibrary}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ThreeDStockLifestylesContent;
